import Swal from "sweetalert2";

// Centralised alerts. Success feedback is a non-blocking toast; errors, warnings
// and confirmations are dialogs. All share the "jx-" theme in styles/SweetAlert.css.

const ICONS = {
  success: '<i class="bi bi-check-lg"></i>',
  error: '<i class="bi bi-x-lg"></i>',
  warning: '<i class="bi bi-exclamation-lg"></i>',
  info: '<i class="bi bi-info-lg"></i>',
  question: '<i class="bi bi-question-lg"></i>',
  danger: '<i class="bi bi-trash3"></i>',
  logout: '<i class="bi bi-box-arrow-right"></i>',
};

const Dialog = Swal.mixin({
  buttonsStyling: false,
  reverseButtons: true,
  focusCancel: false,
  showClass: { popup: "jx-pop-in" },
  hideClass: { popup: "jx-pop-out" },
  customClass: {
    container: "jx-container",
    popup: "jx-popup",
    icon: "jx-icon",
    title: "jx-title",
    htmlContainer: "jx-text",
    actions: "jx-actions",
    confirmButton: "btn-primary jx-btn",
    cancelButton: "btn-secondary jx-btn",
    denyButton: "btn-danger jx-btn",
    loader: "jx-loader",
    timerProgressBar: "jx-progress",
  },
});

const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  showCloseButton: true,
  timer: 3500,
  timerProgressBar: true,
  buttonsStyling: false,
  showClass: { popup: "jx-toast-in" },
  hideClass: { popup: "jx-toast-out" },
  customClass: {
    container: "jx-toast-container",
    popup: "jx-toast",
    icon: "jx-toast-icon",
    title: "jx-toast-title",
    htmlContainer: "jx-toast-text",
    closeButton: "jx-toast-close",
    timerProgressBar: "jx-progress",
  },
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Swal.stopTimer);
    toast.addEventListener("mouseleave", Swal.resumeTimer);
  },
});

const dialog = (icon, title, message, extra = {}) =>
  Dialog.fire({
    icon: icon === "danger" || icon === "logout" ? "warning" : icon,
    iconHtml: ICONS[icon],
    title,
    text: message,
    confirmButtonText: "Got it",
    ...extra,
    customClass: {
      container: "jx-container",
      popup: `jx-popup jx-tone-${icon}`,
      icon: "jx-icon",
      title: "jx-title",
      htmlContainer: "jx-text",
      actions: "jx-actions",
      confirmButton: `${
        icon === "danger" || icon === "logout" ? "btn-danger-solid" : "btn-primary"
      } jx-btn`,
      cancelButton: "btn-secondary jx-btn",
      loader: "jx-loader",
      ...(extra.customClass || {}),
    },
  });

// Toast notification (non-blocking)
export const showToast = (message, icon = "success", title) =>
  Toast.fire({
    icon,
    iconHtml: ICONS[icon],
    title: title || message,
    text: title ? message : undefined,
    customClass: {
      container: "jx-toast-container",
      popup: `jx-toast jx-tone-${icon}`,
      icon: "jx-toast-icon",
      title: "jx-toast-title",
      htmlContainer: "jx-toast-text",
      closeButton: "jx-toast-close",
      timerProgressBar: "jx-progress",
    },
  });

// Success feedback — a toast. Resolves immediately so callers can `await` it
// and then navigate without waiting for the toast to disappear.
export const showSuccess = (message, title = "Success") => {
  showToast(message, "success", title);
  return Promise.resolve({ isConfirmed: true });
};

export const showError = (message, title = "Something went wrong") =>
  dialog("error", title, message, { confirmButtonText: "Okay" });

export const showWarning = (message, title = "Please check") =>
  dialog("warning", title, message, { confirmButtonText: "Okay" });

export const showInfo = (message, title = "Heads up") =>
  dialog("info", title, message, { confirmButtonText: "Got it" });

// Confirmation dialog. tone: "question" | "danger" | "logout" | "warning"
export const showConfirmation = (
  message,
  title = "Are you sure?",
  confirmButtonText = "Yes, continue",
  cancelButtonText = "Cancel",
  tone = "question"
) =>
  dialog(tone, title, message, {
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText,
  });

// Delete confirmation (specialised)
export const showDeleteConfirmation = (itemType = "item", itemName) =>
  dialog(
    "danger",
    `Delete ${itemType}?`,
    itemName
      ? `"${itemName}" will be permanently deleted. This can't be undone.`
      : `This ${itemType} will be permanently deleted. This can't be undone.`,
    {
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Keep it",
    }
  );

// Blocking loader
export const showLoading = (message = "Please wait a moment", title = "Working on it") =>
  Dialog.fire({
    title,
    text: message,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    customClass: {
      container: "jx-container",
      popup: "jx-popup jx-loading",
      title: "jx-title",
      htmlContainer: "jx-text",
      loader: "jx-loader",
    },
    didOpen: () => Swal.showLoading(),
  });

export const closeLoading = () => Swal.close();

const sweetAlert = {
  showSuccess,
  showError,
  showWarning,
  showInfo,
  showConfirmation,
  showLoading,
  closeLoading,
  showToast,
  showDeleteConfirmation,
};

export default sweetAlert;
